import { Injectable } from '@angular/core';

import { AESEncryptionService } from '@services/aes-encryption.service';
import { ISuperAdminTokens } from '@shared/model/super-admin.model';

const SESSION_KEYS = {
    accessToken: 'sa.at',
    refreshToken: 'sa.rt',
} as const;

/**
 * Super Admin session, deliberately isolated from the tenant session (Step 11):
 * - sessionStorage, not localStorage: tenant logout calls localStorage.clear(), and an
 *   admin session should end with the tab.
 * - Own keys, never reading tenant tokens, so a tenant token can't reach Super Admin APIs.
 * JWT expiry is read only to decide when to show the login page; the backend verifies the
 * signature and actor on every request.
 */
@Injectable({
    providedIn: 'root',
})
export class SuperAdminSessionService {
    constructor(private readonly encryptionService: AESEncryptionService) {}

    setTokens(tokens: ISuperAdminTokens): void {
        this.write(SESSION_KEYS.accessToken, tokens.accessToken);
        this.write(SESSION_KEYS.refreshToken, tokens.refreshToken);
    }

    setAccessToken(accessToken: string): void {
        this.write(SESSION_KEYS.accessToken, accessToken);
    }

    getAccessToken(): string | null {
        return this.read(SESSION_KEYS.accessToken);
    }

    getRefreshToken(): string | null {
        return this.read(SESSION_KEYS.refreshToken);
    }

    /** Signed in while the refresh token is unexpired; an expired access token is refreshed on use. */
    isAuthenticated(): boolean {
        const refreshToken = this.getRefreshToken();
        return !!refreshToken && !this.isExpired(refreshToken);
    }

    clear(): void {
        Object.values(SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key));
    }

    private isExpired(token: string): boolean {
        try {
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            return typeof payload?.exp !== 'number' || Date.now() >= payload.exp * 1000;
        } catch {
            return true;
        }
    }

    private write(key: string, value: string): void {
        sessionStorage.setItem(key, this.encryptionService.encrypt(value));
    }

    private read(key: string): string | null {
        const value = sessionStorage.getItem(key);
        if (!value) {
            return null;
        }
        try {
            return this.encryptionService.decrypt(value);
        } catch {
            return null;
        }
    }
}
